import type { UserRole } from '../../types';
const TOKEN_OPS = 'business_admin';
const TOKEN_WS = 'workspace_admin';
const LEGACY_ADMIN_UI_LABEL = ['WORKSPACE', 'ADMIN'].join(' ');

/**
 * Display-only key space for membership badges. Extends the canonical account membership
 * union with scoped-relationship display slugs (delivery, ally, supplier, capital).
 * This type is for PRESENTATION ONLY and never authorizes
 * anything — authorization flows through `scopedCapabilityResolver`.
 */
export type RoleDisplayKey =
  | UserRole
  | 'organization_member'
  | 'project_manager'
  | 'partner'
  | 'vendor'
  | 'investor';

export type RoleCategory = 'internal' | 'external' | 'platform' | 'scoped';

export interface RoleDisplayConfig {
  label: string;
  icon: string;
  color: string;       // Tailwind color classes for badge backgrounds
  iconColor: string;   // Tailwind color classes for icons
  bgColor: string;     // Flat background color (gradients removed per design policy)
  category: RoleCategory;
  description?: string;
}

export const ROLE_DISPLAY: Record<RoleDisplayKey, RoleDisplayConfig> = {
  platform_owner: {
    label: 'PLATFORM OWNER',
    icon: "crown",
    color: 'bg-status-critical-soft text-status-critical-foreground border-status-critical',
    iconColor: 'text-status-danger-foreground',
    bgColor: 'bg-status-critical',
    category: 'internal',
    description: 'AX1 control-plane ownership and emergency access'
  },
  platform_admin: {
    label: 'PLATFORM ADMIN',
    icon: "shield",
    color: 'bg-status-caution-soft text-status-caution-foreground border-status-caution',
    iconColor: 'text-status-caution-foreground',
    bgColor: 'bg-status-caution',
    category: 'internal',
    description: 'Licensing, onboarding, tenant operations and support'
  },
  admin: {
    label: 'PLATFORM ADMIN',
    icon: "crown",
    color: 'bg-status-critical-soft text-status-critical-foreground border-status-critical',
    iconColor: 'text-status-danger-foreground',
    bgColor: 'bg-status-critical',
    category: 'internal',
    description: 'Legacy alias mapped to Platform Admin'
  },
  [TOKEN_OPS]: {
    label: LEGACY_ADMIN_UI_LABEL,
    icon: "briefcase",
    color: 'bg-status-warning-soft text-status-warning-foreground border-status-warning',
    iconColor: 'text-status-warning-foreground',
    bgColor: 'bg-status-warning',
    category: 'internal',
    description: 'Legacy compatibility membership shown with the canonical Workspace Admin label'
  },
  system_admin: {
    label: 'SYSTEM ADMIN',
    icon: "shield",
    color: 'bg-status-caution-soft text-status-caution-foreground border-status-caution',
    iconColor: 'text-status-caution-foreground',
    bgColor: 'bg-status-caution',
    category: 'internal',
    description: 'Technical platform, security and incident management'
  },
  security_admin: {
    label: 'SECURITY ADMIN',
    icon: "shieldCheck",
    color: 'bg-status-caution-soft text-status-caution-foreground border-status-caution',
    iconColor: 'text-status-caution-foreground',
    bgColor: 'bg-status-caution',
    category: 'internal',
    description: 'Identity, privileged access, security events and assurance'
  },
  support_admin: {
    label: 'SUPPORT ADMIN',
    icon: "lifeBuoy",
    color: 'bg-status-info-soft text-status-info-foreground border-status-info',
    iconColor: 'text-status-info-foreground',
    bgColor: 'bg-status-info',
    category: 'internal',
    description: 'Account support and redacted diagnostics'
  },
  commercial_admin: {
    label: 'COMMERCIAL ADMIN',
    icon: "creditCard",
    color: 'bg-status-warning-soft text-status-warning-foreground border-status-warning',
    iconColor: 'text-status-warning-foreground',
    bgColor: 'bg-status-warning',
    category: 'internal',
    description: 'Contracts, packages, allowances, billing and renewals'
  },
  implementation_admin: {
    label: 'IMPLEMENTATION ADMIN',
    icon: "rocket",
    color: 'bg-status-info-soft text-status-info-foreground border-status-info',
    iconColor: 'text-status-info-foreground',
    bgColor: 'bg-status-info',
    category: 'internal',
    description: 'Time-bounded onboarding, imports and configuration'
  },
  organization_admin: {
    label: 'WORKSPACE ADMIN',
    icon: "building2",
    color: 'bg-accent-soft text-accent-primary border-accent-primary',
    iconColor: 'text-accent-primary',
    bgColor: 'bg-primary-500',
    category: 'internal',
    description: 'Coordinates people, workspace configuration and operations'
  },
  organization_member: {
    label: 'WORKSPACE MEMBER',
    icon: "users",
    color: 'border-border-subtle bg-surface-muted text-text-secondary',
    iconColor: 'text-text-muted',
    bgColor: 'bg-status-neutral',
    category: 'internal',
    description: 'Customer organization workspace membership'
  },
  customer_security_admin: {
    label: 'CUSTOMER SECURITY ADMIN',
    icon: "lock",
    color: 'bg-status-caution-soft text-status-caution-foreground border-status-caution',
    iconColor: 'text-status-caution-foreground',
    bgColor: 'bg-status-caution',
    category: 'internal',
    description: 'Customer identity, access, audit and security policy'
  },
  project_manager: {
    label: 'PROJECT MANAGER',
    icon: "target",
    color: 'bg-accent-soft text-accent-primary border-primary-200',
    iconColor: 'text-primary-600',
    bgColor: 'bg-primary-500',
    category: 'internal',
    description: 'Manage projects, teams, and deliverables'
  },
  [TOKEN_WS]: {
    label: LEGACY_ADMIN_UI_LABEL,
    icon: "settings",
    color: 'border-border-subtle bg-surface-muted text-text-secondary',
    iconColor: 'text-text-muted',
    bgColor: 'bg-status-neutral',
    category: 'internal',
    description: 'Participants and configuration for a named workspace'
  },
  business_dev_manager: {
    label: 'PARTNER',
    icon: "briefcase",
    color: 'bg-status-success-soft text-status-success-foreground border-status-success',
    iconColor: 'text-status-success-foreground',
    bgColor: 'bg-status-success',
    category: 'internal',
    description: 'Legacy alias mapped to Partner'
  },
  team_member: {
    label: 'VENDOR',
    icon: "user",
    color: 'bg-status-caution-soft text-status-caution-foreground border-status-caution',
    iconColor: 'text-status-caution-foreground',
    bgColor: 'bg-status-caution',
    category: 'internal',
    description: 'Legacy alias mapped to Vendor'
  },
  member: {
    label: 'VENDOR',
    icon: "wrench",
    color: 'bg-accent-soft text-accent-primary border-primary-200',
    iconColor: 'text-primary-600',
    bgColor: 'bg-primary-500',
    category: 'external',
    description: 'Legacy alias mapped to Vendor'
  },
  partner: {
    label: 'PARTNER',
    icon: "users",
    color: 'bg-status-success-soft text-status-success-foreground border-status-success',
    iconColor: 'text-status-success-foreground',
    bgColor: 'bg-status-success',
    category: 'external',
    description: 'Collaborate on shared projects'
  },
  vendor: {
    label: 'VENDOR',
    icon: "wrench",
    color: 'bg-accent-soft text-accent-primary border-primary-200',
    iconColor: 'text-primary-600',
    bgColor: 'bg-primary-500',
    category: 'external',
    description: 'Deliver assigned work, evidence and documents'
  },
  investor: {
    label: 'INVESTOR',
    icon: "trendingUp",
    color: 'bg-status-success-soft text-status-success-foreground border-status-success',
    iconColor: 'text-status-success-foreground',
    bgColor: 'bg-status-success',
    category: 'external',
    description: 'Portfolio visibility and capital oversight'
  }
};
